import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import HeroSlider from "../ui/HeroSlider";
import Gallery from "../ui/Gallery";
import FeatureAccordion from "../ui/FeatureAccordion";
import { Truck, Plane, Car } from "lucide-react";
import {
  Users,
  Settings,
  FileText,
  DollarSign,
  ArrowRight,
  Check,
  Sparkles,
  MessageSquare,
  CalendarCheck,
  Settings2,
  MapPin,
  Hammer,
  Ruler,
  Paintbrush,
  Clock,
  ShieldCheck,
  Award,
  Zap,
  Shield,
} from "lucide-react";

const Home = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const services = [
    {
      icon: Hammer,
      title: "Carpentry & Joinery",
      description: "Custom cabinetry, furniture and woodworking solutions.",
      link: "/services?tab=carpentry",
    },
    {
      icon: Truck,
      title: "TRANSPORT & LOGISTICS ",
      description:
        "Reliable transport solutions for individuals and businesses.",
      link: "/services?tab=transport",
    },
    {
      icon: Plane,
      title: "AIRPORT TRANSFERS & SHUTTLES",
      description:
        "Comfortable and timely airport pickup and drop-off services.",
      link: "/services?tab=airport",
    },
    {
      icon: Car,
      title: "VEHICLE RENTALS",
      description:
        "Affordable short-term and long-term vehicle rental options.",
      link: "/services?tab=rentals",
    },
  ];

  const slides = [
    {
      title: "Transform Your Space",
      description: "Innovative services tailored to your needs.",
      image: "/img/slider2.webp",
      cta: "View Portfolio",
      ctaLink: "/products",
    },
    {
      title: "Exceptional Customer Service",
      description:
        "Dedicated to providing a seamless experience from consultation to delivery.",
      image: "/img/airport1.jpg",
      cta: "Our Services",
      ctaLink: "/services",
    },
  ];

  const features = [
    {
      icon: Award,
      title: "Master Craftsmanship",
      description:
        "Whether it's custom cabinetry or safe transport, we deliver precision in every task.",
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: ShieldCheck,
      title: "Reliability & Safety",
      description:
        "Fully insured services and professional drivers ensure your assets are always protected.",
      color: "from-purple-500 to-pink-500",
    },
    {
      icon: Zap,
      title: "Efficient Logistics",
      description:
        "Streamlined scheduling for airport transfers and shuttles to keep you on time, every time.",
      color: "from-green-500 to-emerald-500",
    },
    {
      icon: Users,
      title: "Client-Centric Approach",
      description:
        "Tailored solutions for homes and businesses with a 98% satisfaction rate.",
      color: "from-amber-500 to-orange-500",
    },
  ];

  const processSteps = [
    {
      icon: MessageSquare,
      title: "Consultation",
      description:
        "We discuss your specific needs, whether it's a custom build or transport logistics.",
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: CalendarCheck,
      title: "Planning & Booking",
      description:
        "Scheduling, material selection, or route planning to ensure a seamless experience.",
      color: "from-purple-500 to-pink-500",
    },
    {
      icon: Settings2,
      title: "Preparation",
      description:
        "Setting up workshops or readying vehicles to meet our high-performance standards.",
      color: "from-amber-500 to-orange-500",
    },
    {
      icon: Truck,
      title: "Service Execution",
      description:
        "Professional delivery, whether crafting your furniture or driving you to the airport.",
      color: "from-green-500 to-emerald-500",
    },
    {
      icon: MapPin,
      title: "Delivery & Setup",
      description:
        "Final placement or arrival at your destination with minimal disruption.",
      color: "from-indigo-500 to-purple-500",
    },
    {
      icon: ShieldCheck,
      title: "Final Verification",
      description:
        "Quality inspections and client walkthroughs to ensure 100% satisfaction.",
      color: "from-rose-500 to-red-500",
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          Stash Masters | Carpentry, Transport & Vehicle Rental Services in
          Windhoek
        </title>

        <meta
          name="description"
          content="Stash Masters provides carpentry and joinery, transport services, airport transfers, shuttle services, and vehicle rentals in Windhoek, Namibia."
        />
        <meta
          name="keywords"
          content="Stash Masters, carpentry Windhoek, joinery Namibia, transport services Windhoek, airport transfers Namibia, shuttle services Windhoek, vehicle rentals Namibia"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Stash Masters" />

        <meta
          property="og:title"
          content="Stash Masters | Carpentry, Transport & Vehicle Rental Services in Windhoek"
        />
        <meta
          property="og:description"
          content="Professional carpentry, joinery, transport services, airport transfers, shuttle services, and vehicle rentals in Windhoek, Namibia."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://stash-masters.com/" />
        <meta
          property="og:image"
          content="https://stash-masters.com/img/logo2.png"
        />
        <meta property="og:site_name" content="Stash Masters" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Stash Masters | Carpentry, Transport & Vehicle Rental Services in Windhoek"
        />
        <meta
          name="twitter:description"
          content="Professional carpentry, joinery, transport services, airport transfers, shuttle services, and vehicle rentals in Windhoek, Namibia."
        />
        <meta
          name="twitter:image"
          content="https://stash-masters.com/img/logo2.png"
        />

        <link rel="canonical" href="https://stash-masters.com/" />
      </Helmet>

      <div className="overflow-hidden">
        {/* Hero Section */}
        <HeroSlider slides={slides} />

        {/* Features Grid - Enhanced for Multi-Service Business */}
        <section className="py-20 bg-gradient-to-b from-gray-50 via-white to-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/5 rounded-full mb-6">
                <Sparkles size={18} className="text-primary" />
                <span className="text-primary font-semibold text-sm tracking-wider">
                  WHY CHOOSE US
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Excellence in Every{" "}
                <span className="relative">
                  <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                    Service
                  </span>
                  <div className="absolute bottom-2 left-0 w-full h-3 bg-primary/10 -rotate-1 rounded-full -z-10"></div>
                </span>
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                We combine professional mastery with modern logistics to provide
                seamless solutions that enhance your lifestyle and business
                operations.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 max-w-7xl mx-auto">
              {features.map((feature) => {
                const IconComponent = feature.icon;
                return (
                  <div
                    key={feature.title}
                    className="group relative bg-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100"
                  >
                    <div
                      className={`absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r ${feature.color} rounded-t-3xl`}
                    ></div>

                    <div
                      className={`relative mb-6 inline-block p-3 bg-gradient-to-br ${feature.color} bg-opacity-10 rounded-2xl`}
                    >
                      <div
                        className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300`}
                      ></div>
                      <IconComponent
                        size={32}
                        className="text-gray-800 group-hover:scale-110 transition-transform duration-300"
                        strokeWidth={1.5}
                      />
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors duration-300">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>

                    <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div
                        className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-full blur-xl opacity-20`}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="py-24 bg-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="relative">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="/img/slider2.webp"
                    alt="Craftsmanship"
                    className="w-full h-[500px] object-cover transform hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent"></div>
                </div>

                <div className="absolute -bottom-6 -right-6 sm:-bottom-8 sm:-right-8 bg-white p-6 sm:p-8 rounded-2xl shadow-2xl border border-gray-100">
                  <div className="text-4xl sm:text-5xl font-bold text-primary mb-1">
                    6
                  </div>
                  <div className="text-gray-600 text-sm sm:text-base">
                    Years of Excellence
                  </div>
                </div>
              </div>

              <div className="space-y-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/5 rounded-full">
                  <Sparkles size={18} className="text-accent" />
                  <span className="text-accent font-semibold text-sm tracking-wider">
                    ABOUT US
                  </span>
                </div>

                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  Reliable Services for{" "}
                  <span className="relative">
                    <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                      Homes & Businesses
                    </span>
                    <div className="absolute bottom-2 left-0 w-full h-3 bg-accent/10 rotate-1 rounded-full -z-10"></div>
                  </span>
                </h2>

                <p className="text-gray-600 text-lg leading-relaxed">
                  We provide a combination of skilled craftsmanship and
                  dependable transport solutions. From custom carpentry and
                  joinery work to professional transport, airport transfers,
                  shuttle services, and vehicle rentals, our goal is to deliver
                  practical solutions that make life easier for our clients.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    "Custom Carpentry & Joinery Solutions",
                    "Reliable Transport & Logistics Services",
                    "Airport Transfers & Shuttle Transport",
                    "Flexible Vehicle Rental Options",
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3 group">
                      <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-primary to-accent rounded-lg flex items-center justify-center transform group-hover:scale-110 transition-transform">
                        <Check
                          size={16}
                          className="text-white"
                          strokeWidth={3}
                        />
                      </div>
                      <span className="text-gray-700 font-medium text-sm sm:text-base">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>

                <a
                  href="/contact"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary to-accent text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 group"
                >
                  <span>Contact Us Today</span>
                  <ArrowRight
                    size={20}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-gray-50">
          <div className="container mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-12">
              Our Services
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service) => {
                const Icon = service.icon;
                return (
                  <a
                    key={service.title}
                    href={service.link}
                    className="bg-white p-8 rounded-2xl shadow hover:shadow-xl transition"
                  >
                    <Icon className="mb-4 text-primary" size={32} />
                    <h3 className="font-bold text-xl mb-2">{service.title}</h3>
                    <p className="text-gray-600 text-sm">
                      {service.description}
                    </p>
                  </a>
                );
              })}
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section className="py-24 bg-gradient-to-b from-gray-900 to-gray-800 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <img
              src="/img/logo.png"
              alt=""
              aria-hidden="true"
              className="floating-logo drift1"
            />
            <img
              src="/img/logo.png"
              alt=""
              aria-hidden="true"
              className="floating-logo drift2"
            />
            <img
              src="/img/logo.png"
              alt=""
              aria-hidden="true"
              className="floating-logo drift3"
            />
            <img
              src="/img/logo.png"
              alt=""
              aria-hidden="true"
              className="floating-logo drift4"
            />
          </div>

          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
                backgroundSize: "40px 40px",
              }}
            ></div>
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full backdrop-blur-sm mb-6">
                <Sparkles size={18} className="text-primary" />
                <span className="text-white/90 font-semibold text-sm tracking-wider">
                  PORTFOLIO
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                Featured{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                  Projects
                </span>
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed">
                Explore our latest work showcasing exceptional craftsmanship and
                innovative design solutions
              </p>
            </div>

            <Gallery />

            <div className="text-center mt-16">
              <a
                href="/services"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-gray-900 font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 group border-2 border-transparent hover:border-primary/20"
              >
                <span>View Full Portfolio</span>
                <ArrowRight
                  size={20}
                  className="group-hover:translate-x-2 transition-transform"
                />
              </a>
            </div>
          </div>
        </section>

        {/* How We Work Section - Multi-Service Journey */}
        {/* <section className="py-24 bg-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-50 via-white to-gray-50"></div>

          <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-primary/5 to-transparent"></div>
          <div className="absolute bottom-0 right-0 w-full h-64 bg-gradient-to-t from-accent/5 to-transparent"></div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/5 rounded-full mb-6">
                <Sparkles size={18} className="text-primary" />
                <span className="text-primary font-semibold text-sm tracking-wider">
                  OUR PROCESS
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                How We{" "}
                <span className="relative">
                  <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                    Deliver
                  </span>
                  <div className="absolute bottom-2 left-0 w-full h-3 bg-primary/10 -rotate-1 rounded-full -z-10"></div>
                </span>
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed max-w-2xl mx-auto">
                A structured, reliable approach to carpentry and transport,
                ensuring excellence at every stage of your service experience.
              </p>
            </div>

            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                {processSteps.map((step, index) => {
                  const IconComponent = step.icon;
                  return (
                    <div
                      key={step.title}
                      className="group relative bg-white rounded-2xl p-6 sm:p-8 shadow-md hover:shadow-xl transition-all duration-500 border border-gray-100 hover:border-transparent"
                    >
                      <div
                        className={`absolute inset-0 bg-gradient-to-r ${step.color} opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-500 -z-10`}
                        style={{ padding: "2px" }}
                      >
                        <div className="absolute inset-0 bg-white rounded-2xl"></div>
                      </div>

                      <div className="relative">
                        <div className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center text-gray-500 font-bold text-sm sm:text-base">
                          {index + 1}
                        </div>

                        <div
                          className={`relative mb-5 inline-block p-3 sm:p-4 bg-gradient-to-br ${step.color} bg-opacity-10 rounded-xl sm:rounded-2xl`}
                        >
                          <div
                            className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-0 group-hover:opacity-20 rounded-xl sm:rounded-2xl transition-opacity duration-300`}
                          ></div>
                          <IconComponent
                            size={28}
                            className="text-gray-800 group-hover:scale-110 transition-transform duration-300 sm:w-8 sm:h-8"
                            strokeWidth={1.5}
                          />
                        </div>

                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3 group-hover:text-primary transition-colors duration-300">
                          {step.title}
                        </h3>
                        <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section> */}
      </div>
    </>
  );
};

export default Home;
